import {
  faCheck,
  faSpinner,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { StorylineApi } from '../../API/StorylineApi';
import { useAuth } from '../../Auth/AuthProvider';
import Image from '../../Components/Image';
import { StorylineDilemmaOption } from '../../Types/StorylineDilemma';

interface StorylineDilemmaTextOptionCardProps {
  option: StorylineDilemmaOption;
  isSelected: boolean;
  onClick: (option: StorylineDilemmaOption) => void;
  onOptionDeleted?: (optionId: string) => void;
}

export const StorylineDilemmaTextOptionCard: React.FC<
  StorylineDilemmaTextOptionCardProps
> = ({ option, isSelected, onClick, onOptionDeleted }) => {
  const { userId } = useAuth();
  const [isDeletionInProgress, setIsDeletionInProgress] = useState(false);

  const handleDeleteOption = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const confirmMessage = `Are you sure you want to delete this dilemma option?`;

    if (window.confirm(confirmMessage)) {
      setIsDeletionInProgress(true);
      try {
        const storylineApi = new StorylineApi();
        await storylineApi.deleteDilemmaOption(option.dilemmaId, option.id);
        onOptionDeleted?.(option.id);
      } catch (error) {
        console.error('Failed to delete dilemma option', {
          optionId: option.id,
          error,
        });
      } finally {
        setIsDeletionInProgress(false);
      }
    }
  };

  const isUserCreated = option.userId === userId;

  return (
    <div
      data-testid={`storyline-dilemma-text-option-card`}
      className="
            flex flex-col justify-center w-full text-sm rounded-xl max-md:mt-3 cursor-pointer bg-gray-50 border border-gray-200
            max-lg:mt-0
            sm:grow
            relative
          "
    >
      {isUserCreated && (
        <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-2">
          {!isDeletionInProgress ? (
            <div
              className="option-delete-button w-8 h-8"
              onClick={handleDeleteOption}
            >
              <div className="flex justify-center items-center w-8 h-8 text-center text-white whitespace-nowrap bg-red-500 rounded-lg">
                <FontAwesomeIcon icon={faTrashCan} className="w-3 text-white" />
              </div>
            </div>
          ) : (
            <div className="w-8 h-8 flex justify-center items-center">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin h-5 w-5 mr-1 align-middle text-white"
              />
            </div>
          )}
        </div>
      )}

      <div
        className={`
              flex overflow-hidden relative flex-col w-full h-[180px]
          `}
        onClick={() => onClick(option)}
      >
        {option.image && (
          <div className={`absolute inset-0 bg-gray-100 rounded-xl`}>
            <Image
              src={[
                option.image.thumbnailUrl,
                option.image.compressedUrl,
                option.image.originalUrl,
                option.image.sharedStorageUrl,
              ]}
              loader={
                <div className="h-full w-full animate-shimmer rounded-xl" />
              }
              retries={60 * 60 * 24}
              alt={option.title}
              className={`object-cover absolute inset-0 rounded-xl size-full`}
            />
          </div>
        )}
        {!option.image && (
          <div
            className={`flex absolute px-2 py-2 tracking-wide text-start text-gray-500 text-ellipsis italic`}
          >
            <div>Hang tight! Image is coming soon...</div>
          </div>
        )}
        {isSelected && (
          <>
            {!isUserCreated && (
              <div className="absolute right-2.5 top-2.5 bg-blue-600 rounded-full w-10 h-10 flex justify-center items-center">
                <FontAwesomeIcon icon={faCheck} color={'white'} />
              </div>
            )}
            <div
              className={`absolute top-0 bottom-0 left-0 right-0 rounded-xl z-[1] ${isSelected ? 'shadow-[0_0_0_8px_rgba(37,99,235)_inset]' : 'border border-gray-200'}`}
            ></div>
          </>
        )}
        <div
          className={`absolute flex flex-col top-0 left-0 w-full h-full ${
            !option.image
              ? 'text-gray-800'
              : 'bg-gradient-to-t from-black via-black-500/25 to-transparent text-white'
          } rounded-xl`}
          style={{
            backgroundSize: '100% 80%',
            backgroundPosition: 'bottom',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className={`px-3 pb-2 mt-auto`}>
            <div
              className={`relative mb-auto text-left text-base font-medium max-md:mt-5 max-md:mr-2.5`}
            >
              {option.title}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
